import { html, nothing, type TemplateResult } from "lit";
import { icons } from "../icons.ts";
import { createCopyButton, copySelectionWithin, type ClipboardLike } from "./copy-as-markdown.ts";
import type { MessageRewindTarget } from "./message-transcript.ts";

export type MessageActionHandlers = {
  onEditUser?: (target: MessageRewindTarget, draft: string) => void;
  onRegenerate?: () => void;
  /**
   * Toggle the per-message manual collapse. When the message is currently
   * collapsed, the button is rendered as "Expand"; when expanded, "Collapse".
   * Implementation lives in the parent (which owns the collapsed-state set)
   * so the same state can drive the bubble's preview line.
   */
  onToggleCollapse?: () => void;
  /**
   * Whether the message is currently in the manual-collapsed state. When
   * `true`, the action button is rendered as "Expand"; otherwise "Collapse".
   */
  isCollapsed?: boolean;
  /**
   * Optional: returns the DOM root for the bubble that owns these actions.
   * The "Copy selection" button uses it to scope `getSelection()` so the
   * copied text never bleeds outside the bubble (e.g. into the composer
   * textarea). When omitted, "Copy selection" is not rendered.
   */
  bubbleScope?: () => Element | Document | null;
  /**
   * Optional clipboard override used by tests; defaults to
   * `navigator.clipboard` at call time.
   */
  clipboard?: ClipboardLike;
};

type ActionEntry = {
  key: string;
  label: string;
  icon: TemplateResult;
  title: string;
  danger?: boolean;
  pressed?: boolean;
  onClick: (e: Event) => void;
};

/**
 * BIBLE §3 — *Liczba widocznych przycisków jest ograniczona* and
 * *Dodatkowe działania pojawiają się po najechaniu na obiekt jako popover*.
 *
 * The audit found 7+ always-rendered controls per assistant message
 * (Edit / Regenerate / Collapse / Copy / Copy selection / Copy JSON /
 * Open in viewer). That is a wall of chrome around every bubble.
 *
 * This renderer keeps exactly ONE visible control per message — a `⋯`
 * overflow button — and moves the rest into a popover that opens on
 * hover, on click, and on keyboard (Enter/Space on the trigger, Escape
 * to close, arrow keys to move between entries).
 */
export function renderMessageActions(params: {
  role: "user" | "assistant" | "other";
  markdown?: string | null;
  rewindTarget?: MessageRewindTarget | null;
  userDraft?: string | null;
  isLastAssistant?: boolean;
  isCollapsed?: boolean;
  handlers?: MessageActionHandlers;
}): ReturnType<typeof html> | typeof nothing {
  const handlers = params.handlers;
  if (!handlers) {
    return nothing;
  }

  const canCopy = Boolean(params.markdown?.trim());
  const canEdit =
    params.role === "user" && Boolean(params.rewindTarget) && Boolean(handlers.onEditUser);
  const canRegenerate =
    params.role === "assistant" && params.isLastAssistant && Boolean(handlers.onRegenerate);
  const canCopySelection = canCopy && typeof handlers.bubbleScope === "function";
  const canToggleCollapse = Boolean(handlers.onToggleCollapse) && Boolean(params.markdown?.trim());

  if (!canCopy && !canEdit && !canRegenerate && !canCopySelection && !canToggleCollapse) {
    return nothing;
  }

  const entries: ActionEntry[] = [];

  if (canEdit) {
    entries.push({
      key: "edit",
      label: "Edit",
      icon: icons.edit,
      title: "Edit this message (rewind on send after you confirm)",
      onClick: () => handlers.onEditUser?.(params.rewindTarget!, params.userDraft ?? ""),
    });
  }

  if (canRegenerate) {
    entries.push({
      key: "regenerate",
      label: "Regenerate",
      icon: icons.refreshCw,
      title: "Regenerate this reply",
      onClick: () => handlers.onRegenerate?.(),
    });
  }

  if (canToggleCollapse) {
    entries.push({
      key: "collapse",
      label: params.isCollapsed ? "Expand" : "Collapse",
      icon: params.isCollapsed ? icons.maximize : icons.minimize,
      title: params.isCollapsed ? "Expand this message" : "Collapse this message to a preview",
      pressed: params.isCollapsed,
      onClick: (e: Event) => {
        e.stopPropagation();
        handlers.onToggleCollapse?.();
      },
    });
  }

  if (canCopy) {
    entries.push({
      key: "copy",
      label: "Copy",
      icon: icons.copy,
      title: "Copy this message as Markdown",
      onClick: (e: Event) => {
        e.stopPropagation();
      },
    });
  }

  if (canCopySelection) {
    entries.push({
      key: "copy-selection",
      label: "Copy selection",
      icon: icons.copySelection,
      title: "Copy only the highlighted text in this message",
      onClick: (e: Event) => {
        e.stopPropagation();
        const btn = e.currentTarget as HTMLButtonElement | null;
        const scope = handlers.bubbleScope?.() ?? null;
        void copySelectionWithin(scope, handlers.clipboard).then((copied) => {
          if (!btn) return;
          btn.dataset.copied = copied ? "1" : "0";
          btn.setAttribute("aria-live", "polite");
          window.setTimeout(() => {
            if (!btn.isConnected) return;
            delete btn.dataset.copied;
          }, 1500);
        });
      },
    });
  }

  const popoverId = `chat-message-actions-${params.role}-${Math.random().toString(36).slice(2, 8)}`;

  return html`
    <div
      class="chat-message-actions"
      role="toolbar"
      aria-label="Message actions"
      data-testid="chat-message-actions"
    >
      <button
        type="button"
        class="chat-message-actions__btn chat-message-actions__trigger"
        title="More actions"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded="false"
        aria-controls=${popoverId}
        data-testid="chat-message-actions-trigger"
        @click=${(e: Event) => {
          e.stopPropagation();
          const host = (e.currentTarget as HTMLElement).closest(".chat-message-actions");
          const pop = host?.querySelector<HTMLElement>(".chat-message-actions__menu");
          const trigger = host?.querySelector<HTMLElement>(".chat-message-actions__trigger");
          if (!pop || !trigger) return;
          const open = pop.hasAttribute("hidden");
          if (open) {
            pop.removeAttribute("hidden");
            trigger.setAttribute("aria-expanded", "true");
            pop.querySelector<HTMLElement>(".chat-message-actions__item")?.focus();
          } else {
            pop.setAttribute("hidden", "");
            trigger.setAttribute("aria-expanded", "false");
          }
        }}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const host = (e.currentTarget as HTMLElement).closest(".chat-message-actions");
            const pop = host?.querySelector<HTMLElement>(".chat-message-actions__menu");
            const trigger = host?.querySelector<HTMLElement>(".chat-message-actions__trigger");
            if (!pop || !trigger) return;
            pop.removeAttribute("hidden");
            trigger.setAttribute("aria-expanded", "true");
            const items = Array.from(pop.querySelectorAll<HTMLElement>(".chat-message-actions__item"));
            const current = items.findIndex((el) => el === document.activeElement);
            const next = e.key === "ArrowDown" ? current + 1 : current - 1;
            items[(next + items.length) % items.length]?.focus();
          }
        }}
      >
        ${icons.menu}
        <span class="sr-only">More actions</span>
      </button>
      <div
        class="chat-message-actions__menu"
        id=${popoverId}
        role="menu"
        aria-label="Message actions"
        hidden
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            const host = (e.currentTarget as HTMLElement).closest(".chat-message-actions");
            const pop = host?.querySelector<HTMLElement>(".chat-message-actions__menu");
            const trigger = host?.querySelector<HTMLElement>(".chat-message-actions__trigger");
            pop?.setAttribute("hidden", "");
            trigger?.setAttribute("aria-expanded", "false");
            trigger?.focus();
            return;
          }
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const items = Array.from(
              (e.currentTarget as HTMLElement).querySelectorAll<HTMLElement>(
                ".chat-message-actions__item",
              ),
            );
            const current = items.findIndex((el) => el === document.activeElement);
            const next = e.key === "ArrowDown" ? current + 1 : current - 1;
            items[(next + items.length) % items.length]?.focus();
          }
        }}
        @click=${(e: Event) => {
          // Clicking outside a menu item closes it.
          const target = e.target as HTMLElement | null;
          if (target?.closest(".chat-message-actions__item")) return;
          const host = (e.currentTarget as HTMLElement).closest(".chat-message-actions");
          const pop = host?.querySelector<HTMLElement>(".chat-message-actions__menu");
          const trigger = host?.querySelector<HTMLElement>(".chat-message-actions__trigger");
          pop?.setAttribute("hidden", "");
          trigger?.setAttribute("aria-expanded", "false");
        }}
      >
        ${entries.map(
          (entry) => html`
            <button
              type="button"
              class="chat-message-actions__item ${entry.danger ? "is-danger" : ""}"
              role="menuitem"
              title=${entry.title}
              aria-label=${entry.label}
              ?aria-pressed=${entry.pressed === undefined ? undefined : String(entry.pressed)}
              data-testid="chat-message-action-${entry.key}"
              @click=${(e: Event) => {
                entry.onClick(e);
                const host = (e.currentTarget as HTMLElement).closest(".chat-message-actions");
                const pop = host?.querySelector<HTMLElement>(".chat-message-actions__menu");
                const trigger = host?.querySelector<HTMLElement>(".chat-message-actions__trigger");
                pop?.setAttribute("hidden", "");
                trigger?.setAttribute("aria-expanded", "false");
              }}
            >
              ${entry.icon}
              <span class="chat-message-actions__item-label">${entry.label}</span>
            </button>
          `,
        )}
      </div>
    </div>
  `;
}

/**
 * Keep the standalone copy button helper importable for callers that need
 * a plain copy button (not the overflow menu).
 */
export { createCopyButton };
